import React, { useState } from 'react';
import styles from './WorksheetDocument.module.css';

function getTypeIcon(type) {
  switch (type) {
    case 'multiple_choice': return 'MC';
    case 'true_false': return 'T/F';
    case 'short_answer': return 'SA';
    case 'open_ended': return 'OE';
    default: return '?';
  }
}

function renderQuestionContent(question, showSolution, setShowSolution) {
  switch (question.questionType) {
    case 'multiple_choice':
    case 'true_false':
      return (
        <div className={styles.optionsList}>
          {(question.options || [])
            .sort((a, b) => a.order - b.order)
            .map((opt, i) => (
              <div
                key={i}
                className={`${styles.option} ${opt.isCorrect ? styles.correctOption : ''}`}
              >
                <span className={styles.optionLabel}>{String.fromCharCode(65 + i)})</span>
                <span>{opt.text}</span>
                {opt.isCorrect && <span className={styles.checkmark}>&#10003;</span>}
              </div>
            ))}
        </div>
      );
    case 'short_answer':
      return question.correctAnswer ? (
        <div className={styles.expectedAnswer}>Expected: {question.correctAnswer}</div>
      ) : null;
    case 'open_ended':
      return question.workedSolution ? (
        <div className={styles.solutionSection}>
          <button
            className={styles.solutionToggle}
            onClick={() => setShowSolution(!showSolution)}
            aria-label={showSolution ? 'Hide Solution' : 'Show Solution'}
          >
            {showSolution ? '\u25BC Hide Solution' : '\u25B6 Show Solution'}
          </button>
          {showSolution && (
            <div className={styles.workedSolution}>{question.workedSolution}</div>
          )}
        </div>
      ) : null;
    default:
      return null;
  }
}

function QuestionCard({ question, index, onUpdate, onDelete, onAddStandard, onRemoveStandard }) {
  const [showSolution, setShowSolution] = useState(false);
  const [editing, setEditing] = useState(false);

  return (
    <div className={styles.questionCard}>
      <div className={styles.questionHeader}>
        <span className={styles.questionNumber}>{index}.</span>
        <span className={styles.questionTypeIcon}>{getTypeIcon(question.questionType)}</span>
        <div className={styles.questionMeta}>
          <span className={styles.pointsBadge}>
            {question.points} pt{question.points !== 1 ? 's' : ''}
          </span>
          <span className={styles.difficultyBadge}>{question.difficulty}</span>
        </div>
        <div className={styles.questionActions}>
          <button
            onClick={() => setEditing(!editing)}
            aria-label={editing ? 'Done editing' : 'Edit'}
          >
            {editing ? 'Done' : 'Edit'}
          </button>
          <button onClick={onDelete} aria-label="Delete">
            Delete
          </button>
        </div>
      </div>

      {editing ? (
        <textarea
          className={styles.questionTextEdit}
          value={question.questionText}
          onChange={(e) => onUpdate({ questionText: e.target.value })}
        />
      ) : (
        <p className={styles.questionText}>{question.questionText}</p>
      )}

      {renderQuestionContent(question, showSolution, setShowSolution)}

      {question.questionStandards?.length > 0 && (
        <div className={styles.standardChips}>
          {question.questionStandards.map((qs) => (
            <span key={qs.standard.id} className={styles.chip}>
              {qs.standard.standardCode}
              <button
                onClick={() => onRemoveStandard(qs.standard.id)}
                aria-label="Remove standard"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function AddQuestionButton({ onAdd }) {
  const [showMenu, setShowMenu] = useState(false);

  const questionTypes = [
    { type: 'multiple_choice', label: 'Multiple Choice' },
    { type: 'true_false', label: 'True/False' },
    { type: 'short_answer', label: 'Short Answer' },
    { type: 'open_ended', label: 'Open-Ended' },
  ];

  return (
    <div className={styles.addQuestionWrapper}>
      <button
        className={styles.addQuestionButton}
        onClick={() => setShowMenu(!showMenu)}
        aria-label="Add question"
      >
        + Add Question
      </button>
      {showMenu && (
        <div className={styles.addQuestionMenu}>
          {questionTypes.map(({ type, label }) => (
            <button
              key={type}
              className={styles.addQuestionOption}
              onClick={() => {
                onAdd(type);
                setShowMenu(false);
              }}
              aria-label={label}
            >
              <span className={styles.typeIconSmall}>{getTypeIcon(type)}</span>
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function WorksheetDocument({
  worksheet,
  questions,
  onUpdate,
  onUpdateQuestion,
  onDeleteQuestion,
  onAddQuestion,
  onAddQuestionStandard,
  onRemoveQuestionStandard,
}) {
  const totalPoints = questions.reduce((sum, q) => sum + (q.points || 1), 0);

  return (
    <div className={styles.document}>
      {/* Header */}
      <div className={styles.header}>
        <input
          className={styles.titleInput}
          value={worksheet.title || ''}
          onChange={(e) => onUpdate('title', e.target.value)}
          placeholder="Worksheet title"
        />
        <div className={styles.badges}>
          <span className={styles.typeBadge}>{worksheet.type}</span>
          <span className={styles.difficultyBadge}>{worksheet.difficulty}</span>
          <span className={styles.pointsBadge}>{totalPoints} pts</span>
        </div>
      </div>

      {/* Instructions */}
      <textarea
        className={styles.instructions}
        value={worksheet.instructions || ''}
        onChange={(e) => onUpdate('instructions', e.target.value)}
        placeholder="Instructions for students (optional)"
      />

      {/* Question List */}
      <div className={styles.questionList}>
        {questions.length === 0 ? (
          <p className={styles.emptyState}>No questions yet. Add one below.</p>
        ) : (
          questions
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .map((question, index) => (
              <QuestionCard
                key={question.id}
                question={question}
                index={index + 1}
                onUpdate={(data) => onUpdateQuestion(question.id, data)}
                onDelete={() => onDeleteQuestion(question.id)}
                onAddStandard={(standard) => onAddQuestionStandard(question.id, standard)}
                onRemoveStandard={(standardId) => onRemoveQuestionStandard(question.id, standardId)}
              />
            ))
        )}
      </div>

      {/* Add Question */}
      <AddQuestionButton onAdd={onAddQuestion} />
    </div>
  );
}

export default WorksheetDocument;
